import { alpha, darken } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import type { TeamSide } from './types';

export const COMPACT_WIDGET_WIDTH_MEDIA_QUERY = '(max-width: 479.95px)';
export const COMPACT_WIDGET_HEIGHT_MEDIA_QUERY = '(max-height: 480px)';
export const COMPACT_WIDGET_MEDIA_QUERY = `${COMPACT_WIDGET_WIDTH_MEDIA_QUERY}, ${COMPACT_WIDGET_HEIGHT_MEDIA_QUERY}`;
export const COMPACT_WIDGET_VIEWPORT_QUERY = `@media ${COMPACT_WIDGET_MEDIA_QUERY}`;
export const MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY = '@media (max-width: 479.95px) and (min-height: 480.01px)';
export const TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY =
  '@media (min-width: 480px) and (max-width: 899.95px) and (orientation: portrait)';
export const MOBILE_TABBED_WIDGET_MEDIA_QUERY = COMPACT_WIDGET_MEDIA_QUERY;
export const TABLET_WIDGET_VIEWPORT_QUERY = '@media (min-width: 480px) and (max-width: 899.95px)';
export const SHORT_DESKTOP_VIEWPORT_QUERY = '@media (min-width: 900px) and (max-height: 760px)';
export const TALL_DESKTOP_VIEWPORT_QUERY = '@media (min-width: 900px) and (min-height: 760.01px)';
const MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT = '100dvh';
const MOBILE_LANDSCAPE_CONTENT_MAX_HEIGHT = 'calc(100dvh - 214px)';
const TABLET_CONTENT_MAX_HEIGHT = 'calc(100dvh - 214px)';
const DESKTOP_CONTENT_MAX_HEIGHT = 'calc(90dvh - 250px)';
const SHORT_DESKTOP_CONTENT_MAX_HEIGHT = 'calc(100dvh - 218px)';
const TALL_DESKTOP_WIDGET_HEIGHT = '600px';
const TALL_DESKTOP_CONTENT_HEIGHT = 'calc(600px - 218px)';
const MOBILE_TABBED_WIDGET_QUERY = `@media ${MOBILE_TABBED_WIDGET_MEDIA_QUERY}`;
const SUMMARY_COURT_ASPECT_RATIO = '520 / 292';
const SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER = '1.781';
const MOBILE_PORTRAIT_CONTENT_HEIGHT = `calc(100vw / ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER})`;
export const MOBILE_PORTRAIT_WIDGET_HEIGHT = `calc(${MOBILE_PORTRAIT_CONTENT_HEIGHT} + 214px)`;
export const TABLET_PORTRAIT_WIDGET_WIDTH = '470px';
const TABLET_PORTRAIT_CONTENT_HEIGHT = `calc(${TABLET_PORTRAIT_WIDGET_WIDTH} / ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER})`;
export const TABLET_PORTRAIT_WIDGET_HEIGHT = `calc(${TABLET_PORTRAIT_CONTENT_HEIGHT} + 214px)`;
export const TALL_DESKTOP_WIDGET_MAX_WIDTH = `calc(${TALL_DESKTOP_CONTENT_HEIGHT} * ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER})`;
export const PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH = 'min(100%, clamp(260px, calc(178dvh - 435px), 340px))';
export const PRE_MATCH_WIDGET_MAX_WIDTH = {
  xs: 'min(100%, clamp(220px, calc(178dvh - 435px), 100vw))',
  md: '56vw',
};
const SUMMARY_COURT_MAX_WIDTH = {
  xs: '100%',
  md: 'min(100%, calc(145dvh - 435px))',
};

export const WIDGET_COLORS = {
  shell: 'var(--pmw-shell)',
  frame: 'var(--pmw-frame)',
  header: 'var(--pmw-header)',
  headerInner: 'var(--pmw-header-inner)',
  slate: 'var(--pmw-slate)',
  meta: 'var(--pmw-meta)',
  panel: 'var(--pmw-panel)',
  panelSoft: 'var(--pmw-panel-soft)',
  panelStrong: 'var(--pmw-panel-strong)',
  line: 'var(--pmw-line)',
  text: 'var(--pmw-text)',
  muted: 'var(--pmw-muted)',
  subtle: 'var(--pmw-subtle)',
  accent: 'var(--pmw-accent)',
  danger: 'var(--pmw-danger)',
  warning: 'var(--pmw-warning)',
  success: 'var(--pmw-success)',
  pitchDark: 'var(--pmw-pitch-dark)',
  pitchLight: 'var(--pmw-pitch-light)',
  overlaySoft: 'var(--pmw-overlay-soft)',
  overlayBorder: 'var(--pmw-overlay-border)',
  overlayStrong: 'var(--pmw-overlay-strong)',
  overlayField: 'var(--pmw-overlay-field)',
  overlayFade: 'var(--pmw-overlay-fade)',
  overlayScrollbar: 'var(--pmw-overlay-scrollbar)',
  accentSurface: 'var(--pmw-accent-surface)',
  accentBorder: 'var(--pmw-accent-border)',
  accentSoft: 'var(--pmw-accent-soft)',
  shadow: 'var(--pmw-shadow)',
  pitchBase: 'var(--pmw-pitch-base)',
  pitchLine: 'var(--pmw-pitch-line)',
  pitchLineSoft: 'var(--pmw-pitch-line-soft)',
  pitchGlow: 'var(--pmw-pitch-glow)',
  pitchShade: 'var(--pmw-pitch-shade)',
} as const;

const normalizeThemeColor = (color?: string) => {
  if (!color) return color;

  return /^[0-9A-Fa-f]{6}$/.test(color) ? `#${color}` : color;
};

const getWidgetThemeColors = (theme: Theme) => {
  const primaryMain = normalizeThemeColor(theme.palette.primary.main) ?? '#2B25BB';
  const secondaryMain = normalizeThemeColor(theme.palette.secondary.main) ?? '#FEDA2B';
  const infoMain = normalizeThemeColor(theme.palette.info.main) ?? primaryMain;
  const errorMain =
    normalizeThemeColor(theme.palette.error.main) ?? normalizeThemeColor(theme.palette.error[500]) ?? '#FA6565';
  const successMain = normalizeThemeColor(theme.palette.success.main) ?? primaryMain;
  const warningMain = normalizeThemeColor(theme.palette.warning.main) ?? secondaryMain;
  const neutral900 = normalizeThemeColor(theme.palette.neutral?.[900]) ?? '#181818';
  const neutral800 = normalizeThemeColor(theme.palette.neutral?.[800]) ?? neutral900;
  const neutral700 = normalizeThemeColor(theme.palette.neutral?.[700]) ?? neutral800;
  const neutral600 = normalizeThemeColor(theme.palette.neutral?.[600]) ?? neutral700;
  const neutral300 = normalizeThemeColor(theme.palette.neutral?.[300]) ?? theme.palette.text.secondary ?? '#AAB0B4';
  const textPrimary = normalizeThemeColor(theme.palette.text.primary) ?? '#FFFFFF';
  const textSecondary = normalizeThemeColor(theme.palette.text.secondary) ?? neutral300;
  const textDisabled = normalizeThemeColor(theme.palette.text.disabled) ?? neutral300;
  const backgroundDefault = normalizeThemeColor(theme.palette.background.default) ?? neutral900;
  const backgroundPaper = normalizeThemeColor(theme.palette.background.paper) ?? neutral800;

  return {
    shell: darken(backgroundPaper, 0.18),
    frame: alpha(primaryMain, 0.34),
    header: alpha(primaryMain, 0.14),
    headerInner: alpha(primaryMain, 0.2),
    slate: alpha(neutral600, 0.9),
    meta: alpha(neutral700, 0.94),
    panel: alpha(backgroundPaper, 0.96),
    panelSoft: alpha(primaryMain, 0.12),
    panelStrong: alpha(backgroundDefault, 0.96),
    line: alpha(infoMain, 0.22),
    text: textPrimary,
    muted: textSecondary,
    subtle: textDisabled,
    accent: infoMain,
    danger: errorMain,
    warning: warningMain,
    success: successMain,
    pitchBase: '#0d9048',
    pitchDark: '#0b7c41',
    pitchLight: '#17a35b',
    pitchLine: 'rgba(255, 255, 255, 0.84)',
    pitchLineSoft: 'rgba(255, 255, 255, 0.52)',
    pitchGlow: 'rgba(246, 229, 197, 0.14)',
    pitchShade: 'rgba(0, 0, 0, 0.18)',
    overlaySoft: alpha(textPrimary, 0.06),
    overlayBorder: alpha(textPrimary, 0.08),
    overlayStrong: alpha(textPrimary, 0.12),
    overlayField: alpha(textPrimary, 0.5),
    overlayFade: alpha(textPrimary, 0.45),
    overlayScrollbar: alpha(textPrimary, 0.18),
    accentSurface: alpha(infoMain, 0.14),
    accentBorder: alpha(infoMain, 0.3),
    accentSoft: alpha(infoMain, 0.12),
    shadow: alpha(backgroundDefault, 0.42),
  };
};

export const getWidgetColorVariables = (theme: Theme) => {
  const colors = getWidgetThemeColors(theme);

  return {
    '--pmw-shell': colors.shell,
    '--pmw-frame': colors.frame,
    '--pmw-header': colors.header,
    '--pmw-header-inner': colors.headerInner,
    '--pmw-slate': colors.slate,
    '--pmw-meta': colors.meta,
    '--pmw-panel': colors.panel,
    '--pmw-panel-soft': colors.panelSoft,
    '--pmw-panel-strong': colors.panelStrong,
    '--pmw-line': colors.line,
    '--pmw-text': colors.text,
    '--pmw-muted': colors.muted,
    '--pmw-subtle': colors.subtle,
    '--pmw-accent': colors.accent,
    '--pmw-danger': colors.danger,
    '--pmw-warning': colors.warning,
    '--pmw-success': colors.success,
    '--pmw-pitch-dark': colors.pitchDark,
    '--pmw-pitch-light': colors.pitchLight,
    '--pmw-overlay-soft': colors.overlaySoft,
    '--pmw-overlay-border': colors.overlayBorder,
    '--pmw-overlay-strong': colors.overlayStrong,
    '--pmw-overlay-field': colors.overlayField,
    '--pmw-overlay-fade': colors.overlayFade,
    '--pmw-overlay-scrollbar': colors.overlayScrollbar,
    '--pmw-accent-surface': colors.accentSurface,
    '--pmw-accent-border': colors.accentBorder,
    '--pmw-accent-soft': colors.accentSoft,
    '--pmw-shadow': colors.shadow,
    '--pmw-pitch-base': colors.pitchBase,
    '--pmw-pitch-line': colors.pitchLine,
    '--pmw-pitch-line-soft': colors.pitchLineSoft,
    '--pmw-pitch-glow': colors.pitchGlow,
    '--pmw-pitch-shade': colors.pitchShade,
  };
};

export const styles = {
  root: (theme: Theme) => {
    return {
      ...getWidgetColorVariables(theme),
      width: { xs: '100%', sm: 480, md: '100%' },
      maxWidth: { xs: '100%', sm: 480, md: '100%' },
      minWidth: { sm: 480, md: 0 },
      height: '100%',
      maxHeight: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      mx: 'auto',
      borderRadius: '16px',
      border: 'none',
      background: 'transparent',
      boxShadow: 'none',
      overflow: 'hidden',
      boxSizing: 'border-box',
      [TALL_DESKTOP_VIEWPORT_QUERY]: {
        height: TALL_DESKTOP_WIDGET_HEIGHT,
        maxHeight: TALL_DESKTOP_WIDGET_HEIGHT,
      },
      [TABLET_WIDGET_VIEWPORT_QUERY]: {
        height: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
        maxHeight: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      },
      [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
        height: MOBILE_PORTRAIT_WIDGET_HEIGHT,
        maxHeight: MOBILE_PORTRAIT_WIDGET_HEIGHT,
        '& [data-pmw-section="competition"] > *': {
          gap: 0.5,
          px: 1,
          py: 0.5,
        },
        '& [data-pmw-section="tournament"] > *': {
          gap: 0.75,
          px: 1,
          py: 0.75,
        },
      },
      [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
        height: TABLET_PORTRAIT_WIDGET_HEIGHT,
        maxHeight: TABLET_PORTRAIT_WIDGET_HEIGHT,
      },
    };
  },
  constrainedRoot: {
    width: PRE_MATCH_WIDGET_MAX_WIDTH,
    maxWidth: PRE_MATCH_WIDGET_MAX_WIDTH,
    minWidth: { xs: 0, sm: 0 },
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      width: TALL_DESKTOP_WIDGET_MAX_WIDTH,
      maxWidth: `min(100%, ${TALL_DESKTOP_WIDGET_MAX_WIDTH})`,
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      width: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
      maxWidth: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      width: '100%',
      maxWidth: '100%',
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      width: TABLET_PORTRAIT_WIDGET_WIDTH,
      maxWidth: `min(100%, ${TABLET_PORTRAIT_WIDGET_WIDTH})`,
    },
  },
  summaryRoot: {
    height: 'auto',
    flexShrink: 0,
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      height: TALL_DESKTOP_WIDGET_HEIGHT,
      maxHeight: TALL_DESKTOP_WIDGET_HEIGHT,
      flexShrink: 1,
    },
    [TABLET_WIDGET_VIEWPORT_QUERY]: {
      height: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      maxHeight: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      flexShrink: 1,
    },
    [SHORT_DESKTOP_VIEWPORT_QUERY]: {
      height: '100%',
      maxHeight: '100%',
      flexShrink: 1,
      '& [data-pmw-section="competition"] > *': {
        gap: 0.5,
        px: 1.25,
        py: 0.625,
      },
      '& [data-pmw-section="tournament"] > *': {
        gap: 1,
        px: 1.25,
        py: 0.75,
      },
      '& [data-pmw-section="tabs"]': {
        marginTop: 'auto',
      },
      '& [data-pmw-section="tabs"] > *': {
        py: 0.25,
      },
      '& .MuiTab-root': {
        minHeight: 36,
      },
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      height: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      maxHeight: MOBILE_LANDSCAPE_WIDGET_MAX_HEIGHT,
      '& [data-pmw-section="competition"] > *': {
        gap: 0.5,
        px: 1,
        py: 0.5,
      },
      '& [data-pmw-section="tournament"] > *': {
        gap: 0.75,
        px: 1,
        py: 0.75,
      },
      '& [data-pmw-section="tabs"] > *': {
        py: 0.25,
      },
      '& .MuiTab-root': {
        minHeight: 34,
      },
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: MOBILE_PORTRAIT_WIDGET_HEIGHT,
      maxHeight: MOBILE_PORTRAIT_WIDGET_HEIGHT,
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: TABLET_PORTRAIT_WIDGET_HEIGHT,
      maxHeight: TABLET_PORTRAIT_WIDGET_HEIGHT,
    },
  },
  container: {
    gap: 0.75,
    p: 0,
    height: '100%',
    minHeight: 0,
    maxHeight: 'inherit',
    '& [data-pmw-section]': {
      flexShrink: 0,
      width: '100%',
    },
    '& [data-pmw-section="content"]': {
      minHeight: 0,
      flexShrink: 1,
      [TABLET_WIDGET_VIEWPORT_QUERY]: {
        flex: 1,
        display: 'flex',
      },
      [SHORT_DESKTOP_VIEWPORT_QUERY]: {
        flex: 1,
        display: 'flex',
      },
      [TALL_DESKTOP_VIEWPORT_QUERY]: {
        flex: 1,
        display: 'flex',
      },
      [COMPACT_WIDGET_VIEWPORT_QUERY]: {
        flex: 1,
        display: 'flex',
      },
    },
  },
  summaryContainer: {
    height: 'auto',
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
    [TABLET_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
    [SHORT_DESKTOP_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
    },
  },
  headerBar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 1,
    p: 0.5,
    borderRadius: '10px',
    backgroundColor: WIDGET_COLORS.header,
    border: `1px solid ${WIDGET_COLORS.overlaySoft}`,
  },
  trackerPill: {
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    borderRadius: '8px',
    backgroundColor: WIDGET_COLORS.headerInner,
  },
  trackerTitle: {
    typography: 'subtitle2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
  },
  pinBadge: {
    width: 34,
    height: 34,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: WIDGET_COLORS.overlaySoft,
    color: WIDGET_COLORS.danger,
  },
  teamStrip: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 1.5,
    px: 1.5,
    py: 1.25,
    backgroundColor: WIDGET_COLORS.slate,
    color: WIDGET_COLORS.text,
  },
  teamList: {
    gap: 0.5,
    minWidth: 0,
  },
  teamListRow: {
    display: 'flex',
    minWidth: 0,
    alignItems: 'center',
    gap: 0.75,
  },
  teamListLabel: {
    typography: 'subtitle2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
  },
  teamListMeta: {
    typography: 'body2',
    overflowWrap: 'anywhere',
    color: WIDGET_COLORS.text,
  },
  countdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    color: WIDGET_COLORS.text,
  },
  countdownValue: {
    typography: 'h3',
    fontWeight: 800,
    lineHeight: 1,
  },
  countdownLabel: {
    typography: 'h6',
    fontWeight: 600,
    lineHeight: 1,
  },
  kickoffStrip: {
    display: 'grid',
    gap: 0.875,
    px: 1.5,
    py: 0.875,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    borderTop: `1px solid ${WIDGET_COLORS.frame}`,
    borderBottom: `1px solid ${WIDGET_COLORS.frame}`,
    backgroundColor: WIDGET_COLORS.meta,
  },
  kickoffTopRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 1.25,
    alignItems: 'center',
  },
  competitionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.875,
    minWidth: 0,
  },
  competitionPrimary: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  competitionSecondary: {
    typography: 'body2',
    color: WIDGET_COLORS.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  kickoffMeta: {
    textAlign: 'right',
  },
  kickoffPrimary: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
  },
  kickoffSecondary: {
    typography: 'body2',
    color: WIDGET_COLORS.muted,
  },
  venueRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 1,
    alignItems: 'center',
    pt: 0.75,
    borderTop: `1px solid ${WIDGET_COLORS.overlayBorder}`,
  },
  venueInfo: {
    typography: 'h6',
    color: WIDGET_COLORS.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pitch: {
    position: 'relative',
    overflow: 'hidden',
    aspectRatio: '520 / 292',
    width: '100%',
    boxSizing: 'border-box',
    px: { xs: 1, sm: 1.5 },
    py: { xs: 1.25, sm: 1.75 },
    mb: 1.5,
    borderRadius: '12px',
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    backgroundColor: WIDGET_COLORS.pitchBase,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    isolation: 'isolate',
    '&[data-pmw-court="basketball"]': {
      '--pmw-pitch-base': '#d99a5b',
      '--pmw-pitch-dark': '#ca8c4e',
      '--pmw-pitch-light': '#e8ad70',
      '--pmw-court-accent': '#4f789f',
    },
    '&[data-pmw-court="tennis"]': {
      '--pmw-pitch-base': '#df5b22',
      '--pmw-pitch-dark': '#c94f1e',
      '--pmw-pitch-light': '#ec6b2d',
    },
    '&[data-pmw-court="table-tennis"]': {
      '--pmw-pitch-base': '#176fb8',
      '--pmw-pitch-dark': '#0f5f9f',
      '--pmw-pitch-light': '#2784cf',
    },
    '&[data-pmw-court="handball"]': {
      '--pmw-pitch-base': '#22a0d4',
      '--pmw-pitch-dark': '#188dc0',
      '--pmw-pitch-light': '#37b3e3',
    },
    '&[data-pmw-court="volleyball"]': {
      '--pmw-pitch-base': '#d5a06a',
      '--pmw-pitch-dark': '#c48e5b',
      '--pmw-pitch-light': '#e5b17b',
      '--pmw-court-accent': '#0aa89d',
    },
    '&[data-pmw-court="ice-hockey"]': {
      '--pmw-pitch-base': '#d8edf4',
      '--pmw-pitch-dark': '#b8d9e6',
      '--pmw-pitch-light': '#effaff',
      '--pmw-pitch-line': 'rgba(18, 45, 68, 0.78)',
      '--pmw-pitch-line-soft': 'rgba(18, 45, 68, 0.52)',
      '--pmw-court-red': '#ff3d46',
      '--pmw-court-blue': '#1d80bf',
      '--pmw-court-blue-fill': '#0fa7d1',
    },
    '&[data-pmw-court="water-polo"]': {
      '--pmw-pitch-base': '#0f72b8',
      '--pmw-pitch-dark': '#095f9d',
      '--pmw-pitch-light': '#1f91d8',
    },
    '&[data-pmw-court="baseball"]': {
      '--pmw-pitch-base': '#237847',
      '--pmw-pitch-dark': '#1d653c',
      '--pmw-pitch-light': '#2e9258',
    },
  },
  pitchStripes: {
    position: 'absolute',
    inset: 0,
    background: `repeating-linear-gradient(
      90deg,
      ${WIDGET_COLORS.pitchDark} 0%,
      ${WIDGET_COLORS.pitchDark} 7.5%,
      ${WIDGET_COLORS.pitchLight} 7.5%,
      ${WIDGET_COLORS.pitchLight} 15%
    )`,
    opacity: 0.95,
  },
  pitchDimEdge: {
    position: 'absolute',
    inset: 0,
    background: `
      radial-gradient(circle at 50% 50%, transparent 42%, ${WIDGET_COLORS.pitchShade} 100%),
      linear-gradient(180deg, ${WIDGET_COLORS.pitchShade} 0%, transparent 18%, transparent 82%, ${WIDGET_COLORS.pitchShade} 100%)
    `,
    opacity: 0.85,
  },
  pitchNoise: {
    position: 'absolute',
    inset: 0,
    background: `
      radial-gradient(circle at 12% 20%, ${WIDGET_COLORS.pitchGlow} 0, transparent 18%),
      radial-gradient(circle at 88% 20%, ${WIDGET_COLORS.pitchGlow} 0, transparent 18%),
      radial-gradient(circle at 50% 50%, ${WIDGET_COLORS.pitchGlow} 0, transparent 34%),
      radial-gradient(circle at 50% 50%, rgba(255,255,255,0.03) 0, transparent 58%)
    `,
    mixBlendMode: 'screen',
    opacity: 0.9,
  },
  pitchSvg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    color: WIDGET_COLORS.pitchLine,
    zIndex: 0,
  },
  fixtureCard: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: { xs: 260, sm: 300, md: 320 },
    mx: 'auto',
    px: 1.125,
    py: 1.125,
    borderRadius: '12px',
    backgroundColor: WIDGET_COLORS.panelStrong,
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    boxShadow: `0 12px 24px ${WIDGET_COLORS.shadow}`,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      maxWidth: 220,
      px: 0.75,
      py: 0.75,
    },
  },
  fixtureCardRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 0.875,
  },
  fixtureTeam: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.4,
    minWidth: 0,
  },
  fixtureTeamLabel: {
    typography: 'body2',
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    fontWeight: 800,
    color: WIDGET_COLORS.text,
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'normal',
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      typography: 'caption',
    },
  },
  fixtureJerseyPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    color: WIDGET_COLORS.text,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      width: 32,
      height: 32,
    },
  },
  fixtureCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.3,
    minWidth: 72,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      minWidth: 48,
    },
  },
  versus: {
    typography: 'h3',
    fontWeight: 800,
    color: WIDGET_COLORS.overlayFade,
    lineHeight: 1,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      typography: 'h4',
    },
  },
  comparisonSection: {
    mt: 1.25,
    pt: 1,
    borderTop: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      mt: 0.75,
      pt: 0.625,
    },
  },
  comparisonFormsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 0.75,
  },
  comparisonForm: {
    display: 'flex',
    gap: 0.375,
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  comparisonSpacer: {
    width: 72,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      width: 48,
    },
  },
  comparisonDivider: {
    width: '100%',
    height: 1,
    mt: 0.75,
    backgroundColor: WIDGET_COLORS.overlayBorder,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      mt: 0.5,
    },
  },
  comparisonRankingRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 0.75,
    mt: 0.75,
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      mt: 0.5,
    },
  },
  comparisonCenter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    whiteSpace: 'nowrap',
  },
  comparisonLabel: {
    typography: 'caption',
    fontWeight: 600,
    color: WIDGET_COLORS.pitchLineSoft,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    textAlign: 'center',
  },
  comparisonRankValue: {
    typography: 'body2',
    fontWeight: 800,
    color: WIDGET_COLORS.text,
    lineHeight: 1,
    justifySelf: 'center',
    textAlign: 'center',
  },
  contentCard: {
    width: '100%',
    height: 'auto',
    minHeight: { xs: 'auto', sm: 'auto', md: 400 },
    maxHeight: { xs: 'none', sm: 'none', md: DESKTOP_CONTENT_MAX_HEIGHT },
    aspectRatio: { xs: SUMMARY_COURT_ASPECT_RATIO, sm: SUMMARY_COURT_ASPECT_RATIO, md: 'auto' },
    flex: { xs: 0, sm: 0, md: 1 },
    overflowY: 'auto',
    px: 1.5,
    py: 1.5,
    border: `1px solid ${WIDGET_COLORS.line}`,
    backgroundColor: WIDGET_COLORS.panel,
    color: WIDGET_COLORS.text,
    boxSizing: 'border-box',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: WIDGET_COLORS.overlayScrollbar,
      borderRadius: 999,
    },
    [TABLET_WIDGET_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: TABLET_CONTENT_MAX_HEIGHT,
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
      py: 1,
    },
    [SHORT_DESKTOP_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: SHORT_DESKTOP_CONTENT_MAX_HEIGHT,
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
      py: 1,
    },
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: 'none',
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: MOBILE_LANDSCAPE_CONTENT_MAX_HEIGHT,
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
      py: 1,
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: MOBILE_PORTRAIT_CONTENT_HEIGHT,
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
      py: 1,
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: 'auto',
      maxHeight: TABLET_PORTRAIT_CONTENT_HEIGHT,
      minHeight: 0,
      aspectRatio: 'auto',
      flex: 1,
      py: 1,
    },
  },
  summaryContentCard: {
    width: '100%',
    height: { xs: 'auto', sm: 'auto', md: 'auto' },
    minHeight: { xs: 0, sm: 0, md: 0 },
    maxHeight: 'none',
    aspectRatio: 'auto',
    flex: 0,
    overflow: 'hidden',
    p: 0,
    border: 'none',
    backgroundColor: WIDGET_COLORS.pitchBase,
    '& > *': {
      height: 'auto',
      minHeight: 0,
    },
    '& [data-pmw-summary-court="true"]': {
      height: '100%',
      minHeight: 0,
      maxWidth: SUMMARY_COURT_MAX_WIDTH,
      mx: 'auto',
      px: 0,
      py: 0,
      mb: 0,
      borderRadius: 0,
      border: 'none',
    },
    [TABLET_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: TABLET_CONTENT_MAX_HEIGHT,
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-court="true"]': {
        width: '100%',
        maxWidth: `min(100%, calc(${TABLET_CONTENT_MAX_HEIGHT} * ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER}))`,
      },
    },
    [SHORT_DESKTOP_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: SHORT_DESKTOP_CONTENT_MAX_HEIGHT,
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-court="true"]': {
        width: '100%',
        maxWidth: 'min(100%, calc((100dvh - 218px) * 1.781))',
      },
      '& [data-pmw-summary-card="true"]': {
        maxWidth: 260,
        px: 1,
        py: 1,
      },
    },
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: 'none',
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-court="true"]': {
        width: '100%',
        maxWidth: `min(100%, ${TALL_DESKTOP_WIDGET_MAX_WIDTH})`,
      },
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: MOBILE_LANDSCAPE_CONTENT_MAX_HEIGHT,
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-card="true"]': {
        maxWidth: 220,
        px: 0.75,
        py: 0.75,
      },
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: MOBILE_PORTRAIT_CONTENT_HEIGHT,
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-court="true"]': {
        width: '100%',
        maxWidth: `min(100%, calc(${MOBILE_PORTRAIT_CONTENT_HEIGHT} * ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER}))`,
      },
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      minHeight: 0,
      maxHeight: TABLET_PORTRAIT_CONTENT_HEIGHT,
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& [data-pmw-summary-court="true"]': {
        width: '100%',
        maxWidth: `min(100%, calc(${TABLET_PORTRAIT_CONTENT_HEIGHT} * ${SUMMARY_COURT_ASPECT_RATIO_MULTIPLIER}))`,
      },
    },
  },
  summaryViewport: {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    minHeight: 0,
    mx: 'auto',
  },
  panelHeader: {
    typography: 'subtitle1',
    fontWeight: 800,
    color: WIDGET_COLORS.text,
    textAlign: 'center',
    mb: 1.5,
  },
  summaryDescription: {
    typography: 'body2',
    color: WIDGET_COLORS.muted,
  },
  standingsTable: {
    width: '100%',
    tableLayout: 'fixed',
    '& .MuiTableCell-root': {
      px: { xs: 0.5, sm: 0.75 },
      py: { xs: 0.5, sm: 0.875 },
      borderBottom: `1px solid ${WIDGET_COLORS.overlayBorder}`,
      color: WIDGET_COLORS.text,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableCell-head': {
      color: WIDGET_COLORS.muted,
      fontWeight: 700,
      textTransform: 'uppercase',
      typography: { xs: 'body2', sm: 'body2' },
    },
    '& .MuiTableCell-body': {
      typography: { xs: 'body2', sm: 'body2' },
    },
  },
  standingsViewTabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 0.25,
    mb: 1.25,
    p: 0.375,
    borderRadius: '10px',
    backgroundColor: WIDGET_COLORS.panelStrong,
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
  },
  standingsViewTab: {
    appearance: 'none',
    width: '100%',
    px: 0.625,
    py: 0.625,
    borderRadius: '8px',
    typography: 'body2',
    textAlign: 'center',
    color: WIDGET_COLORS.muted,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  standingsViewTabActive: {
    backgroundColor: WIDGET_COLORS.accentSurface,
    color: WIDGET_COLORS.accent,
    borderColor: WIDGET_COLORS.accentBorder,
    fontWeight: 700,
  },
  standingsSectionTitle: {
    typography: 'h6',
    fontWeight: 800,
    color: WIDGET_COLORS.text,
    textAlign: 'center',
    mb: 1.25,
  },
  standingsTableWrapper: {
    width: '100%',
    overflowX: 'hidden',
    borderRadius: '10px',
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    backgroundColor: WIDGET_COLORS.accentSurface,
  },
  standingsPositionCell: {
    width: { xs: 28, sm: 34 },
    fontWeight: 800,
  },
  standingsTeamCell: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 0.75 },
    minWidth: 0,
    width: '100%',
  },
  standingsZoneMarker: {
    width: 3,
    minWidth: 3,
    alignSelf: 'stretch',
    borderRadius: '999px',
  },
  standingsTeamLabel: {
    typography: { xs: 'body2', sm: 'body2' },
    color: WIDGET_COLORS.text,
    fontWeight: 700,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  standingsLegend: {
    display: 'grid',
    gap: 0.625,
    mt: 1.25,
    px: 1,
  },
  standingsLegendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  standingsLegendSwatch: {
    width: 14,
    height: 14,
    borderRadius: '2px',
    flexShrink: 0,
  },
  standingsLegendLabel: {
    typography: 'body2',
    color: WIDGET_COLORS.text,
  },
  highlightedTableRow: {
    backgroundColor: WIDGET_COLORS.panelSoft,
  },
  teamPanels: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: { xs: 0.875, sm: 1.25 },
  },
  matchPanelsShell: {
    display: 'grid',
    gap: 1,
  },
  matchPanelsSingle: {
    minWidth: 0,
    width: '100%',
  },
  mobileTeamTabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 0.25,
    p: 0.375,
    borderRadius: '10px',
    backgroundColor: WIDGET_COLORS.panelStrong,
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
  },
  mobileTeamTab: {
    appearance: 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 0.625,
    minWidth: 0,
    px: 0.625,
    py: 0.625,
    borderRadius: '8px',
    color: WIDGET_COLORS.muted,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  mobileTeamTabLeft: {
    justifyContent: 'flex-start',
  },
  mobileTeamTabRight: {
    justifyContent: 'flex-end',
  },
  mobileTeamTabActive: {
    backgroundColor: WIDGET_COLORS.accentSurface,
    color: WIDGET_COLORS.accent,
    borderColor: WIDGET_COLORS.accentBorder,
  },
  mobileTabTeamVisual: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  mobileTeamTabLabel: {
    typography: 'caption',
    fontWeight: 700,
    color: 'inherit',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mobileTeamTabLabelRight: {
    textAlign: 'right',
  },
  mobileAnchoredPanel: {
    minWidth: 0,
    width: 'min(100%, 336px)',
    m: '0 auto',
  },
  mobileAnchoredHeaderRight: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start',
    textAlign: 'right',
  },
  mobileAnchoredTextRight: {
    textAlign: 'right',
  },
  mobileAnchoredTextBlockRight: {
    display: 'block',
    width: 'fit-content',
    maxWidth: '100%',
    ml: 'auto',
    textAlign: 'right',
  },
  mobileAnchoredRowRight: {
    direction: 'rtl',
  },
  mobileAnchoredListGroupRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  mobileAnchoredContentRight: {
    width: 'fit-content',
    maxWidth: '100%',
    alignSelf: 'flex-end',
  },
  mobileAnchoredFullWidth: {
    width: '100%',
    alignSelf: 'stretch',
  },
  mobileAnchoredListItemRight: {
    justifyContent: 'flex-end',
  },
  lineupsShell: {
    display: 'grid',
    gap: 1.25,
  },
  mobileLineupSelector: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 1,
    alignItems: 'center',
  },
  mobileLineupSelectorButton: {
    appearance: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 0.625,
    minWidth: 0,
    p: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: WIDGET_COLORS.muted,
    cursor: 'pointer',
  },
  mobileLineupSelectorButtonLeft: {
    justifyContent: 'flex-start',
  },
  mobileLineupSelectorButtonRight: {
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
  },
  mobileLineupSelectorLabel: {
    typography: 'subtitle2',
    color: 'inherit',
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    opacity: 0.6,
    transition: 'color 180ms ease, opacity 180ms ease',
  },
  mobileLineupSelectorLabelActive: {
    color: WIDGET_COLORS.text,
    opacity: 1,
  },
  mobileLineupSelectorLabelRight: {
    textAlign: 'right',
  },
  mobileLineupSelectorIndicator: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 0.25,
    width: 'min(100%, 128px)',
    mx: 'auto',
  },
  mobileLineupSelectorIndicatorSegment: {
    height: 3,
    borderRadius: 999,
    backgroundColor: WIDGET_COLORS.overlayStrong,
    transition: 'background-color 180ms ease',
  },
  mobileLineupSelectorIndicatorSegmentActive: {
    backgroundColor: WIDGET_COLORS.accent,
  },
  mobileLineupStage: {
    overflow: 'hidden',
  },
  mobileLineupSlide: {
    width: '100%',
  },
  lineupsDesktopHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 164px) minmax(0, 1fr)',
    gap: 1,
    alignItems: 'center',
  },
  lineupsPanels: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: 1.25,
  },
  lineupsTeamSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    minWidth: 0,
    justifyContent: 'center',
  },
  lineupsSummaryTeamVisual: {
    width: 32,
    height: 32,
  },
  lineupsSummaryTeamLabel: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lineupsRefereeCard: {
    display: 'grid',
    gap: 0.25,
    alignItems: 'center',
    justifyItems: 'center',
    p: 1,
    borderRadius: '10px',
    border: `1px solid ${WIDGET_COLORS.accentBorder}`,
    backgroundColor: WIDGET_COLORS.accentSoft,
    textAlign: 'center',
  },
  lineupsRefereeLabel: {
    typography: 'caption',
    color: WIDGET_COLORS.muted,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  lineupsRefereeValue: {
    typography: 'body2',
    color: WIDGET_COLORS.text,
    fontWeight: 700,
    overflowWrap: 'anywhere',
  },
  lineupTeamCard: {
    p: 1.25,
    borderRadius: '10px',
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    backgroundColor: WIDGET_COLORS.accentSurface,
  },
  lineupSections: {
    display: 'grid',
    gap: 1,
  },
  lineupSection: {
    display: 'grid',
    gap: 0.5,
  },
  lineupSectionTitle: {
    typography: 'caption',
    color: WIDGET_COLORS.accent,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    width: 'fit-content',
    px: 0.75,
    py: 0.375,
    borderRadius: '999px',
    border: `1px solid ${WIDGET_COLORS.accentBorder}`,
    backgroundColor: WIDGET_COLORS.accentSurface,
  },
  lineupStaffLabel: {
    typography: 'body2',
    color: WIDGET_COLORS.text,
    fontWeight: 700,
    overflowWrap: 'anywhere',
  },
  lineupEmptyLabel: {
    typography: 'body2',
    color: WIDGET_COLORS.muted,
  },
  teamPanel: {
    p: 1.25,
    borderRadius: '10px',
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    backgroundColor: WIDGET_COLORS.panelSoft,
  },
  teamPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 1,
  },
  teamPanelTitle: {
    typography: 'subtitle2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  playerList: {
    p: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  playerItem: {
    px: 0,
    py: 0.625,
    borderBottom: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    '&:last-child': {
      borderBottom: 'none',
      pb: 0,
    },
  },
  playerName: {
    typography: 'body2',
    color: WIDGET_COLORS.text,
    overflowWrap: 'anywhere',
  },
  summaryBar: {
    display: 'flex',
    height: 4,
    borderRadius: 999,
    overflow: 'hidden',
    mb: 1.25,
    backgroundColor: WIDGET_COLORS.overlayStrong,
  },
  h2hHero: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 1.5,
    mb: 1.5,
    mx: { xs: -1.5, sm: 0 },
    p: 1.25,
    borderRadius: '10px',
    border: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    backgroundColor: WIDGET_COLORS.slate,
    [MOBILE_TABBED_WIDGET_QUERY]: {
      display: 'none',
    },
  },
  h2hHeroTeam: {
    display: 'grid',
    justifyItems: 'center',
    gap: 0.5,
    minWidth: 0,
  },
  h2hHeroTeamRight: {
    justifyItems: 'center',
  },
  h2hHeroTeamVisual: {
    width: 40,
    height: 40,
  },
  h2hHeroTeamLabel: {
    typography: 'subtitle2',
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    color: WIDGET_COLORS.text,
    fontWeight: 800,
    textAlign: 'center',
  },
  h2hHeroVs: {
    typography: { xs: 'h4', sm: 'h3' },
    color: WIDGET_COLORS.muted,
    fontWeight: 800,
    letterSpacing: 1,
    textAlign: 'center',
  },
  matchesMobileHero: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 1.25,
    mx: -1.5,
    px: 1.5,
    py: 1.25,
    backgroundColor: WIDGET_COLORS.slate,
  },
  matchesMobileHeroTeam: {
    display: 'grid',
    justifyItems: 'center',
    gap: 0.375,
    minWidth: 0,
  },
  matchesMobileHeroTeamRight: {
    justifyItems: 'center',
  },
  matchesMobileHeroCenter: {
    display: 'grid',
    justifyItems: 'center',
    gap: 0.25,
  },
  matchesMobileHeroMeta: {
    typography: 'body3',
    color: WIDGET_COLORS.text,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  matchesMobileHeroTeamLabel: {
    typography: 'subtitle2',
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    color: WIDGET_COLORS.text,
    fontWeight: 800,
    textAlign: 'center',
  },
  headToHeadStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 1,
    mb: 1.5,
    alignItems: 'start',
  },
  h2hStatBlock: {
    textAlign: 'center',
  },
  h2hStatValue: {
    typography: 'h4',
    fontWeight: 800,
    lineHeight: 1,
  },
  h2hStatLabel: {
    typography: 'caption',
    textTransform: 'lowercase',
  },
  directMatchList: {
    p: 0,
  },
  directMatchItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    px: 0,
    py: 1,
    width: '100%',
    borderBottom: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    '&:last-child': {
      borderBottom: 'none',
      pb: 0,
    },
  },
  matchMeta: {
    typography: 'caption',
    color: WIDGET_COLORS.muted,
    mb: 0.5,
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
  },
  directMatchRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(72px, max-content) minmax(0, 1fr)',
    alignItems: 'center',
    gap: 1,
    width: '100%',
  },
  directMatchTeamGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    minWidth: 0,
    justifyContent: 'flex-end',
  },
  directMatchTeamGroupRight: {
    justifyContent: 'flex-start',
  },
  directMatchTeamVisual: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  directMatchTeam: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  directMatchTeamMobile: {
    display: { xs: 'block', sm: 'none' },
  },
  directMatchTeamDesktop: {
    display: { xs: 'none', sm: 'block' },
  },
  directMatchTeamRight: {
    textAlign: 'left',
  },
  scorePill: {
    minWidth: 56,
    height: 36,
    px: 1,
    borderRadius: '8px',
    backgroundColor: WIDGET_COLORS.accentSurface,
    color: WIDGET_COLORS.accent,
    typography: 'subtitle2',
    fontWeight: 800,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  formMatchRow: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 0.75,
    py: 0.75,
    borderBottom: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    '&:last-child': {
      borderBottom: 'none',
      pb: 0,
    },
  },
  formMatchFixtureRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0.375,
    width: '100%',
  },
  formMatchMeta: {
    typography: 'caption',
    color: WIDGET_COLORS.muted,
    alignSelf: 'center',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    flex: '0 0 auto',
    width: '100%',
  },
  formMatchTeam: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  formMatchTeamRight: {
    textAlign: 'right',
  },
  formMatchScorePill: {
    minWidth: 56,
    height: 36,
    px: 1,
    borderRadius: '8px',
    typography: 'subtitle2',
    fontWeight: 800,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  formMatchScorePillDraw: {
    backgroundColor: WIDGET_COLORS.warning,
    color: WIDGET_COLORS.panelStrong,
  },
  formMatchOpponent: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  formMatchScore: {
    typography: 'body2',
    color: WIDGET_COLORS.text,
    fontWeight: 700,
  },
  upcomingItem: {
    px: 0,
    py: 1,
    borderBottom: `1px solid ${WIDGET_COLORS.overlayBorder}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0.5,
    '&:last-child': {
      borderBottom: 'none',
      pb: 0,
    },
  },
  selectedTabSurfacePanel: {
    backgroundColor: WIDGET_COLORS.accentSurface,
  },
  upcomingTeams: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
  },
  upcomingCenteredMeta: {
    width: '100%',
    textAlign: 'center',
  },
  upcomingFixtureRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    flexWrap: 'wrap',
    width: '100%',
  },
  upcomingFixtureTeamChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    minWidth: 0,
  },
  upcomingFixtureTeamChipRight: {
    flexDirection: 'row-reverse',
  },
  upcomingFixtureTeamVisual: {
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  upcomingFixtureTeamLabel: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    whiteSpace: 'nowrap',
  },
  upcomingTeam: {
    typography: 'body2',
    fontWeight: 700,
    color: WIDGET_COLORS.text,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  upcomingFixtureDivider: {
    typography: 'caption',
    color: WIDGET_COLORS.muted,
    fontWeight: 700,
    textTransform: 'lowercase',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  tabsShell: {
    px: 0.5,
    py: 0.375,
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
    borderTop: `1px solid ${WIDGET_COLORS.frame}`,
    backgroundColor: WIDGET_COLORS.panelStrong,
  },
  tabs: {
    minHeight: 0,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-list': {
      gap: 0.5,
    },
  },
  iconTab: {
    minWidth: 0,
    minHeight: 42,
    borderRadius: '10px',
    border: '1px solid transparent',
    color: WIDGET_COLORS.muted,
    '&.Mui-selected': {
      color: WIDGET_COLORS.accent,
      borderColor: WIDGET_COLORS.accent,
      backgroundColor: WIDGET_COLORS.accentSoft,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 25,
    },
  },
  teamVisual: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 0,
    flexShrink: 0,
    color: WIDGET_COLORS.text,
  },
  stripTeamVisual: {
    width: 18,
    height: 18,
  },
  panelTeamVisual: {
    width: 22,
    height: 22,
  },
  fixtureTeamVisual: {
    width: 40,
    height: 40,
  },
} as const;

export const getMobileTeamTabSx = (side: TeamSide) => [
  styles.mobileTeamTab,
  side === 'home' ? styles.mobileTeamTabLeft : styles.mobileTeamTabRight,
];

export const getMobileTeamTabLabelSx = (side: TeamSide) => [
  styles.mobileTeamTabLabel,
  side === 'away' && styles.mobileTeamTabLabelRight,
];

export const getMobileAnchoredPanelSx = () => [styles.mobileAnchoredPanel];
